import Constants from 'expo-constants';
import { Platform } from 'react-native';
import { globalState } from '@/state/globalState';
import { supabase } from '@/services/supabase';
import { IS_CHECKLIST_RECOVERY_DIAGNOSTIC } from '@/config/recoveryDiagnostic';
import { isRecoveryRuntimeBlocked, getStableRecoveryDeviceId } from '@/services/recovery/RecoveryRuntimeGate';
import {
  isInitialBootstrapComplete,
  registerPostUpdateSyncObserver,
  type PostUpdateSyncObservation,
} from '@/services/syncService';
import { hasSyncExecution } from '@/services/sync/SyncExecutionLease';
import {
  getClientInstallationPresenceMetadata,
  reportClientInstallationPresence,
} from './clientInstallationPresence';
import {
  collectSupportSynchronizationSummary,
  clearSupportDiagnosticPostUpdatePrepared,
  sendSupportDiagnosticForPostUpdateRequest,
} from '@/services/diagnostics/supportDiagnosticRuntime';
import { sendSupportFullStateForPostUpdateRequest } from '@/services/diagnostics/supportFullStateRuntime';
import { enqueueLegacySupportCheck } from './supportCollectionQueue';
import { compatibleSupportSyncPayload, type SupportSyncPayload } from '@/services/diagnostics/supportSyncSummary';
import {
  postUpdateCollectionStore,
  type PostUpdateIdentity,
  type PostUpdatePlatform,
  type PostUpdateStage,
  type PostUpdateSyncAttempt,
  type PersistedPostUpdateLease,
  type PreparedPostUpdateSummary,
} from './postUpdateCollectionStore';

const PROTOCOL = 1;
const RPC_UNAVAILABLE_TTL_MS = 30 * 60 * 1000;
const STAGES: readonly PostUpdateStage[] = ['sync', 'diagnostic', 'full_state'];
const bootstrapBusyUsers = new Map<string, number>();
const TRANSIENT_FAILURES = new Set([
  'support_offline',
  'support_identity_incomplete',
  'support_session_divergent',
  'support_full_state_unavailable',
  'support_full_state_identity_incomplete',
  'support_full_state_session_divergent',
  'support_full_state_session_unavailable',
  'support_full_state_identity_changed',
  'support_full_state_sync_busy',
  'support_full_state_retry_later',
  'support_full_state_timeout',
  'support_timeout',
  'post_update_bootstrap_busy',
  'network_unavailable',
]);

type RpcRow = Record<string, any>;
type RuntimeScope = PostUpdateIdentity & {
  appVersion: string;
  nativeBuild: number | null;
  compatibilityBuild: number;
  observedBuild: number;
};
type BatchRow = {
  batch_id: string;
  observed_build: number;
  status: string;
  app_version?: string | null;
  activated_at?: string | null;
  expires_at?: string | null;
};
type ActivationRow = {
  batch_id: string;
  created?: boolean;
  sync_request_id?: string | null;
  diagnostic_request_id?: string | null;
  full_state_request_id?: string | null;
  expires_at?: string | null;
};
type StageClaimRow = {
  batch_id: string;
  stage: PostUpdateStage;
  stage_no: number;
  state: string;
  request_id: string | null;
  requested_at?: string | null;
  expires_at?: string | null;
  lease_token?: string | null;
  lease_expires_at?: string | null;
  attempt_no?: number | null;
};
type StageRecordRow = {
  batch_id: string;
  stage: PostUpdateStage;
  state: string;
  next_stage?: PostUpdateStage | null;
};

class PostUpdateCapabilityUnavailableError extends Error {
  constructor() {
    super('post_update_capability_unavailable');
    this.name = 'PostUpdateCapabilityUnavailableError';
  }
}

const first = <T>(data: T | T[] | null | undefined): T | null => Array.isArray(data) ? data[0] ?? null : data ?? null;

const timeout = <T>(promise: PromiseLike<T>, ms = 8_000): Promise<T> => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error('support_timeout')), ms);
  Promise.resolve(promise).then(
    value => { clearTimeout(timer); resolve(value); },
    error => { clearTimeout(timer); reject(error); },
  );
});

export const isPostUpdateRpcUnavailable = (error: unknown): boolean => {
  const candidate = typeof error === 'object' && error !== null
    ? [
      (error as { code?: unknown }).code,
      (error as { status?: unknown }).status,
      (error as { statusCode?: unknown }).statusCode,
      (error as { message?: unknown }).message,
    ]
    : [error];
  const text = candidate.map(value => String(value ?? '')).join(' ').toLowerCase();
  return text.includes('pgrst202')
    || text.includes('42883')
    || text.includes('404')
    || text.includes('function not found')
    || text.includes('could not find the function')
    || text.includes('does not exist');
};

export const isValidPostUpdateSyncOutcome = (value: unknown): value is 'success' | 'partial' | 'error' =>
  value === 'success' || value === 'partial' || value === 'error';

export const shouldDeferPersistedLease = (lease: PersistedPostUpdateLease | null, now = Date.now()): boolean => {
  if (!lease?.leaseExpiresAt) return false;
  const expiresAt = Date.parse(lease.leaseExpiresAt);
  return Number.isFinite(expiresAt) && expiresAt > now;
};

/** Gate efêmero do bootstrap; o resultado persistido continua válido mesmo em erro. */
export const beginPostUpdateBootstrap = (userId?: string | null): (() => void) => {
  const key = userId?.trim();
  if (!key) return () => undefined;
  bootstrapBusyUsers.set(key, (bootstrapBusyUsers.get(key) ?? 0) + 1);
  let ended = false;
  return () => {
    if (ended) return;
    ended = true;
    const count = bootstrapBusyUsers.get(key) ?? 0;
    if (count <= 1) bootstrapBusyUsers.delete(key);
    else bootstrapBusyUsers.set(key, count - 1);
  };
};

export const isPostUpdateBootstrapBusy = (userId?: string | null): boolean =>
  Boolean(userId?.trim() && (bootstrapBusyUsers.get(userId.trim()) ?? 0) > 0);

const safeFailureCode = (error: unknown, stage: PostUpdateStage): string => {
  const raw = error instanceof Error ? error.message : '';
  const known = raw.match(/(?:post_update|support_full_state|support)_[a-z0-9_]+/i)?.[0]?.toLowerCase();
  if (known && /^[a-z0-9_-]{1,40}$/.test(known)) return known;
  const code = typeof error === 'object' && error !== null
    ? String((error as { code?: unknown }).code ?? '').toLowerCase()
    : '';
  if (/^[a-z0-9_-]{1,30}$/.test(code)) return `${stage}_${code}`.slice(0, 40);
  return `${stage}_failed`;
};

const isTransientFailure = (error: unknown): boolean => {
  const raw = error instanceof Error ? error.message : '';
  const code = typeof error === 'object' && error !== null
    ? String((error as { code?: unknown }).code ?? '')
    : '';
  const combined = `${raw} ${code}`.toLowerCase();
  return TRANSIENT_FAILURES.has(raw.toLowerCase())
    || /session_unavailable|identity_changed/.test(combined);
};

const resolveRuntimeScope = (): RuntimeScope | null => {
  if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC || isRecoveryRuntimeBlocked()) return null;
  if (!globalState.network.isConnected.get() || !globalState.database.isInitialized.get()) return null;
  const user = globalState.user.user.get();
  const profile = globalState.user.profile.get();
  const company = globalState.company.data.get();
  const metadata = getClientInstallationPresenceMetadata();
  if (!user?.id || profile?.id !== user.id || !profile.empresa_id || company?.id !== profile.empresa_id || !metadata) return null;
  if (metadata.platform !== 'web' && metadata.nativeBuild === null) return null;
  const observedBuild = metadata.platform === 'web' ? metadata.compatibilityBuild : metadata.nativeBuild!;
  return {
    companyId: profile.empresa_id,
    userId: user.id,
    deviceId: getStableRecoveryDeviceId(),
    platform: metadata.platform,
    appVersion: metadata.appVersion || Constants.expoConfig?.version || 'unknown',
    nativeBuild: metadata.nativeBuild,
    compatibilityBuild: metadata.compatibilityBuild,
    observedBuild,
  };
};

const currentIdentity = (scope: RuntimeScope): PostUpdateIdentity => ({
  companyId: scope.companyId,
  userId: scope.userId,
  deviceId: scope.deviceId,
  platform: scope.platform,
});

const ensureCollectionScope = (scope: RuntimeScope): void => {
  if (hasSyncExecution()) throw new Error('support_full_state_sync_busy');
  if (isPostUpdateBootstrapBusy(scope.userId)) throw new Error('post_update_bootstrap_busy');
  const current = resolveRuntimeScope();
  if (!current
    || current.companyId !== scope.companyId
    || current.userId !== scope.userId
    || current.deviceId !== scope.deviceId
    || current.platform !== scope.platform
    || current.observedBuild !== scope.observedBuild) {
    throw new Error('support_full_state_identity_changed');
  }
};

const rpc = async <T>(name: string, args: Record<string, unknown>): Promise<T> => {
  if (postUpdateCollectionStore.isCapabilityUnavailable()) throw new PostUpdateCapabilityUnavailableError();
  const { data, error } = await timeout(supabase.rpc(name, args));
  if (error) {
    if (isPostUpdateRpcUnavailable(error)) {
      postUpdateCollectionStore.markCapabilityUnavailable(Date.now() + RPC_UNAVAILABLE_TTL_MS);
      throw new PostUpdateCapabilityUnavailableError();
    }
    throw error;
  }
  return data as T;
};

const claimLeaseForBatch = (identity: PostUpdateIdentity, batchId: string, allowResume: boolean): PersistedPostUpdateLease | null => {
  if (!allowResume) return null;
  for (const stage of STAGES) {
    const lease = postUpdateCollectionStore.getLease(identity, batchId, stage);
    // Somente uma lease ainda ativa pode ser retomada com seu token. Lease
    // vencida segue o reclaim normal, usando null na próxima chamada.
    if (lease && shouldDeferPersistedLease(lease, Date.now())) return lease;
  }
  return null;
};

const normalizeLeaseDate = (value: unknown): string | null => {
  if (typeof value !== 'string' || !Number.isFinite(Date.parse(value))) return null;
  return value;
};

const prepareSyncPayload = async (scope: RuntimeScope, requestId: string): Promise<PreparedPostUpdateSummary> => {
  const identity = currentIdentity(scope);
  const cached = postUpdateCollectionStore.getPreparedSummary(identity, requestId);
  if (cached) {
    if (cached.compatiblePayload) {
      const expected = compatibleSupportSyncPayload(cached);
      if (!expected || expected.payloadSha512 !== cached.compatiblePayload.payloadSha512 ||
          JSON.stringify(expected.summary) !== JSON.stringify(cached.compatiblePayload.summary)) {
        throw new Error('post_update_summary_incompatible_cache');
      }
    }
    return cached;
  }
  const prepared = await collectSupportSynchronizationSummary();
  if (prepared.scope.companyId !== scope.companyId || prepared.scope.userId !== scope.userId || prepared.scope.deviceId !== scope.deviceId || prepared.scope.clientBuild !== scope.compatibilityBuild) {
    throw new Error('post_update_summary_identity_changed');
  }
  const cachedSummary = {
    identity,
    requestId,
    clientBuild: scope.compatibilityBuild,
    payloadSha512: prepared.payloadSha512,
    summary: prepared.summary,
    preparedAt: new Date().toISOString(),
  };
  postUpdateCollectionStore.savePreparedSummary(cachedSummary);
  return cachedSummary;
};

const sendSyncStage = async (scope: RuntimeScope, requestId: string, leaseToken: string): Promise<string> => {
  const prepared = await prepareSyncPayload(scope, requestId);
  const submit = async (payload: SupportSyncPayload) => {
    // Preparar o resumo pode atravessar troca de conta/build ou o início de um
    // sync normal; revalidar antes de consumir a lease remota.
    ensureCollectionScope(scope);
    const response = first(await rpc<Array<{ verification_id: string; protocol: string; request_id: string }>>('app_guincho_submit_support_sync_request_v1', {
      p_request_id: requestId,
      p_device_id: scope.deviceId,
      // O claim usa o build atual, mas o payload congelado conserva o build que
      // o preparou (por exemplo, 118 retomado depois da atualização para 119).
      p_client_build: prepared.clientBuild,
      p_lease_token: leaseToken,
      p_payload_sha512: payload.payloadSha512,
      p_summary: payload.summary,
    }));
    if (!response?.verification_id || response.request_id !== requestId || !response.protocol) {
      throw new Error('post_update_sync_receipt_missing');
    }
    return response.verification_id;
  };
  try {
    return await submit(prepared.compatiblePayload ?? prepared);
  } catch (error) {
    const rejection = error as { code?: string; message?: string } | null;
    if (prepared.compatiblePayload || rejection?.code !== 'P0001' || rejection.message !== 'sync_submit_invalid') throw error;
    const compatiblePayload = compatibleSupportSyncPayload(prepared);
    if (!compatiblePayload) throw error;
    ensureCollectionScope(scope);
    // O original fica preservado. Só uma rejeição explícita permite congelar outro formato.
    postUpdateCollectionStore.savePreparedSummary({ ...prepared, compatiblePayload });
    return submit(compatiblePayload);
  }
};

/** Consome o pedido SYNC avulso criado pelo painel (sem lote pós-atualização). */
export const checkPendingSupportSyncRequests = async (_trigger: string): Promise<void> => {
  const scope = resolveRuntimeScope();
  if (!scope || !isInitialBootstrapComplete({ companyId: scope.companyId, userId: scope.userId })) return;
  try {
    ensureCollectionScope(scope);
    const request = first(await rpc<Array<{
      request_id: string;
      requested_at?: string | null;
      expires_at?: string | null;
      lease_token: string | null;
    }>>('app_guincho_claim_support_sync_request_v1', {
      p_empresa_id: scope.companyId,
      p_device_id: scope.deviceId,
      p_client_build: scope.compatibilityBuild,
      p_protocol: PROTOCOL,
    }));
    if (!request?.request_id || !request.lease_token) return;
    const requestLeaseBatchId = `remote-sync:${request.request_id}`;
    postUpdateCollectionStore.saveLease({
      identity: currentIdentity(scope),
      batchId: requestLeaseBatchId,
      stage: 'sync',
      requestId: request.request_id,
      leaseToken: request.lease_token,
      leaseExpiresAt: normalizeLeaseDate(request.expires_at),
      attemptNo: null,
      savedAt: new Date().toISOString(),
    });
    try {
      await sendSyncStage(scope, request.request_id, request.lease_token);
      postUpdateCollectionStore.clearLease(currentIdentity(scope), requestLeaseBatchId, 'sync');
      postUpdateCollectionStore.clearPreparedSummary(currentIdentity(scope), request.request_id);
    } catch (error) {
      // Pedido avulso segue o release específico; o lote usa o registro de
      // etapa. Em ambos os casos o resumo preparado por UUID é preservado.
      try {
        await rpc<boolean>('app_guincho_fail_support_sync_request_v1', {
          p_request_id: request.request_id,
          p_device_id: scope.deviceId,
          p_lease_token: request.lease_token,
          p_error_code: safeFailureCode(error, 'sync'),
        });
      } catch { /* retry/backoff remoto preserva o pedido */ }
    }
  } catch {
    // Ausência da migration nova e pré-condições transitórias não afetam o
    // suporte legado chamado pelo layout.
  }
};

const recordStage = async (
  scope: RuntimeScope,
  batchId: string,
  claim: StageClaimRow,
  outcome: 'fulfilled' | 'failed',
  receiptId: string | null,
  errorCode: string | null,
): Promise<StageRecordRow> => {
  const response = first(await rpc<StageRecordRow[]>('app_guincho_record_post_update_stage_result_v1', {
    p_batch_id: batchId,
    p_stage: claim.stage,
    p_request_id: claim.request_id,
    p_lease_token: claim.lease_token,
    p_outcome: outcome,
    p_receipt_id: receiptId,
    p_error_code: errorCode,
  }));
  if (!response || response.batch_id !== batchId || response.stage !== claim.stage) {
    throw new Error('post_update_stage_record_invalid');
  }
  return response;
};

const runOneStage = async (scope: RuntimeScope, batchId: string, claim: StageClaimRow): Promise<StageRecordRow | null> => {
  if (!claim.request_id || !claim.lease_token) return null;
  ensureCollectionScope(scope);
  const showReceipt = (received: boolean) => {
    if (claim.stage === 'sync') return;
    postUpdateCollectionStore.observePresentationStage(currentIdentity(scope), scope.observedBuild,
      batchId, claim.stage, claim.request_id!, received);
  };
  showReceipt(false);
  postUpdateCollectionStore.saveLease({
    identity: currentIdentity(scope),
    batchId,
    stage: claim.stage,
    requestId: claim.request_id,
    leaseToken: claim.lease_token,
    leaseExpiresAt: normalizeLeaseDate(claim.lease_expires_at),
    attemptNo: typeof claim.attempt_no === 'number' ? claim.attempt_no : null,
    savedAt: new Date().toISOString(),
  });

  let outcome: 'fulfilled' | 'failed' = 'fulfilled';
  let receiptId: string | null = null;
  let errorCode: string | null = null;
  try {
    ensureCollectionScope(scope);
    if (claim.stage === 'sync') receiptId = await sendSyncStage(scope, claim.request_id, claim.lease_token);
    else if (claim.stage === 'diagnostic') {
      const receipt = await sendSupportDiagnosticForPostUpdateRequest(claim.request_id);
      receiptId = receipt?.diagnosticId ?? null;
      if (receipt?.protocol && receiptId) showReceipt(true);
    }
    else {
      const receipt = await sendSupportFullStateForPostUpdateRequest(claim.request_id);
      if (receipt?.protocol) showReceipt(true);
    }
  } catch (error) {
    if (isTransientFailure(error)) throw error;
    outcome = 'failed';
    errorCode = safeFailureCode(error, claim.stage);
    if (claim.stage === 'sync') {
      try {
        await rpc<boolean>('app_guincho_fail_support_sync_request_v1', {
          p_request_id: claim.request_id,
          p_device_id: scope.deviceId,
          p_lease_token: claim.lease_token,
          p_error_code: errorCode,
        });
      } catch {
        // O registro da etapa preserva o retry mesmo se o release dedicado falhar.
      }
    }
  }

  const record = await recordStage(scope, batchId, claim, outcome, receiptId, errorCode);
  if (record.state === 'fulfilled') showReceipt(true);
  postUpdateCollectionStore.clearLease(currentIdentity(scope), batchId, claim.stage);
  if (claim.stage === 'sync' && outcome === 'fulfilled') {
    postUpdateCollectionStore.clearPreparedSummary(currentIdentity(scope), claim.request_id);
  }
  if (claim.stage === 'diagnostic' && outcome === 'fulfilled') {
    try { await clearSupportDiagnosticPostUpdatePrepared(claim.request_id); } catch { /* limpeza não invalida o recibo */ }
  }
  return record;
};

const runBatchStages = async (scope: RuntimeScope, batchId: string, allowResume: boolean): Promise<void> => {
  const identity = currentIdentity(scope);
  let resume = claimLeaseForBatch(identity, batchId, allowResume);
  for (let attempt = 0; attempt < STAGES.length + 1; attempt += 1) {
    ensureCollectionScope(scope);
    const claim = first(await rpc<StageClaimRow[]>('app_guincho_claim_post_update_stage_v1', {
      p_batch_id: batchId,
      p_device_id: scope.deviceId,
      p_client_build: scope.compatibilityBuild,
      p_protocol: PROTOCOL,
      p_resume_lease_token: resume?.leaseToken ?? null,
    }));
    resume = null;
    if (!claim || claim.batch_id !== batchId || !STAGES.includes(claim.stage)) return;

    if (claim.stage !== 'sync' && claim.state === 'fulfilled' && claim.request_id) {
      postUpdateCollectionStore.observePresentationStage(identity, scope.observedBuild,
        batchId, claim.stage, claim.request_id, true);
    }

    if (claim.state === 'fulfilled' || claim.state === 'failed' || !claim.request_id || !claim.lease_token) {
      if (claim.state === 'fulfilled' || claim.state === 'failed') {
        postUpdateCollectionStore.clearLease(identity, batchId, claim.stage);
        if (claim.stage === 'sync' && claim.state === 'fulfilled' && claim.request_id) {
          postUpdateCollectionStore.clearPreparedSummary(identity, claim.request_id);
        }
        continue;
      }
      return;
    }

    const record = await runOneStage(scope, batchId, claim);
    if (!record?.next_stage) return;
    if (record.next_stage === claim.stage) return;
  }
};

const listBatches = async (scope: RuntimeScope): Promise<BatchRow[]> => {
  const data = await rpc<BatchRow[]>('app_guincho_list_post_update_batches_v1', {
    p_device_id: scope.deviceId,
    p_platform: scope.platform,
    p_client_build: scope.compatibilityBuild,
  });
  return Array.isArray(data) ? data : data ? [data as unknown as BatchRow] : [];
};

const activateBatch = async (scope: RuntimeScope, batch: BatchRow, attempt: PostUpdateSyncAttempt): Promise<ActivationRow | null> => {
  const response = first(await rpc<ActivationRow[]>('app_guincho_activate_post_update_collection_v1', {
    p_batch_id: batch.batch_id,
    p_sync_trace_id: attempt.traceId,
    p_sync_started_at: attempt.startedAt,
    p_sync_finished_at: attempt.finishedAt,
    p_sync_result: attempt.outcome,
    p_app_version: attempt.appVersion,
    p_native_build: attempt.nativeBuild,
    p_compatibility_build: attempt.compatibilityBuild,
  }));
  if (!response || response.batch_id !== batch.batch_id) return null;
  return response;
};

const runPostUpdateCollectionOnce = async (scope: RuntimeScope, _trigger: string, allowResume: boolean): Promise<void> => {
  if (postUpdateCollectionStore.isCapabilityUnavailable()) return;
  try { ensureCollectionScope(scope); } catch { return; }
  let batches: BatchRow[];
  try {
    // A primeira consulta pode disputar com a presença disparada após o login.
    await reportClientInstallationPresence();
    ensureCollectionScope(scope);
    batches = await listBatches(scope);
  } catch (error) {
    if (error instanceof PostUpdateCapabilityUnavailableError) return;
    return;
  }

  for (const batch of batches) {
    try { ensureCollectionScope(scope); } catch { return; }
    if (!batch.batch_id || batch.status === 'completed' || batch.status === 'expired') continue;
    if (Number(batch.observed_build) === scope.observedBuild) {
      postUpdateCollectionStore.observePresentationBatch(currentIdentity(scope), scope.observedBuild,
        batch.batch_id, batch.expires_at ?? null);
    }
    const attempt = postUpdateCollectionStore.getFirstSyncAttempt(currentIdentity(scope), scope.observedBuild)
      ?? (scope.observedBuild === Number(batch.observed_build)
        ? postUpdateCollectionStore.getFirstSyncAttempt(currentIdentity(scope), Number(batch.observed_build))
        : null);
    if (!attempt || !isValidPostUpdateSyncOutcome(attempt.outcome)) continue;
    let activated: ActivationRow | null;
    try {
      activated = await activateBatch(scope, batch, attempt);
    } catch (error) {
      if (error instanceof PostUpdateCapabilityUnavailableError) return;
      continue;
    }
    if (!activated) continue;
    try {
      await runBatchStages(scope, batch.batch_id, allowResume);
    } catch {
      // Falhas transitórias mantêm lease/payload para a próxima abertura; as
      // falhas permanentes já foram registradas por runOneStage.
    }
  }
};

let activeRun: Promise<void> | null = null;
let localTail: Promise<void> = Promise.resolve();

const withExecutionMutex = (scope: RuntimeScope, task: (allowResume: boolean) => Promise<void>): Promise<void> => {
  const run = async () => {
    if (Platform.OS === 'web' && typeof navigator !== 'undefined' && (navigator as any).locks?.request) {
      await (navigator as any).locks.request(
        `app-guincho-post-update:${scope.companyId}:${scope.userId}:${scope.deviceId}`,
        { mode: 'exclusive' },
        () => task(true),
      );
      return;
    }
    // Sem Web Locks, nunca reaproveitar o token persistido entre abas. O
    // backend decide a espera/reclaim pela lease; uma única aba ainda usa a
    // fila local para não duplicar seu próprio trabalho.
    await task(Platform.OS !== 'web');
  };
  const next = localTail.catch(() => undefined).then(run);
  localTail = next.catch(() => undefined);
  return next;
};

export const runPostUpdateCollection = (
  trigger: string,
  options: { afterSync?: boolean } = {},
): Promise<void> => {
  const scope = resolveRuntimeScope();
  if (!scope) return Promise.resolve();
  if (!options.afterSync && !isInitialBootstrapComplete({ companyId: scope.companyId, userId: scope.userId })) return Promise.resolve();
  if (activeRun) return activeRun;
  const next = enqueueLegacySupportCheck(() => withExecutionMutex(scope, allowResume => runPostUpdateCollectionOnce(scope, trigger, allowResume)));
  activeRun = next.finally(() => { activeRun = null; });
  return activeRun;
};

const persistFirstAttemptAndRun = async (observation: PostUpdateSyncObservation): Promise<void> => {
  if (!isValidPostUpdateSyncOutcome(observation.outcome)) return;
  const currentUserId = globalState.user.user.get()?.id;
  const currentCompanyId = globalState.user.profile.get()?.empresa_id;
  if (currentUserId !== observation.userId || currentCompanyId !== observation.companyId) return;
  const identity: PostUpdateIdentity = {
    companyId: observation.companyId,
    userId: observation.userId,
    deviceId: getStableRecoveryDeviceId(),
    platform: observation.platform,
  };
  const attempt: PostUpdateSyncAttempt = {
    identity,
    observedBuild: observation.observedBuild,
    nativeBuild: observation.nativeBuild,
    compatibilityBuild: observation.compatibilityBuild,
    appVersion: observation.appVersion,
    traceId: observation.traceId,
    startedAt: observation.startedAt,
    finishedAt: observation.finishedAt,
    outcome: observation.outcome,
  };
  postUpdateCollectionStore.saveFirstSyncAttempt(attempt);
  // A presença pode ter sido iniciada pelo hook legado enquanto a lease ainda
  // estava ativa. Revalidar depois do sync fecha a janela em que o trigger do
  // lote chega somente após a primeira tentativa já ter terminado.
  try { await reportClientInstallationPresence(observation.outcome); } catch { /* presença é best-effort */ }
  await runPostUpdateCollection('sync_completed', { afterSync: true });
};

if (typeof registerPostUpdateSyncObserver === 'function') {
  registerPostUpdateSyncObserver(observation => {
    void persistFirstAttemptAndRun(observation).catch(() => undefined);
  });
}

export const __resetPostUpdateCollectionCoordinatorForTests = (): void => {
  activeRun = null;
  localTail = Promise.resolve();
};
